/**
 * Type-safe feature flag hook for Aegix One.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getFeatureEnabled, type SiteFeatures } from '../services/feature';
import { FeatureCode } from '../types/features';

export const FEATURE_ENABLED_QUERY_KEY = ['featureEnabled'] as const;

export function useFeature(featureCode: FeatureCode): boolean {
  const { data: sitesFeatures } = useQuery<SiteFeatures[]>({
    queryKey: FEATURE_ENABLED_QUERY_KEY,
    queryFn: getFeatureEnabled,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });

  return useMemo(() => {
    if (!sitesFeatures || sitesFeatures.length === 0) return false;

    // Design Decision (ONE vs AIM):
    // In Aegix One (parent/guardian app), guardians may have students enrolled across
    // multiple sites/schools. If ANY site has the feature enabled, the guardian
    // is granted access to the surface across their account.
    return sitesFeatures.some(
      (site) => site.features?.[featureCode] === true,
    );
  }, [sitesFeatures, featureCode]);
}

export function useVisitorManagementEnabled(): boolean {
  return useFeature(FeatureCode.VISITOR_MANAGEMENT);
}
